import {
  BreifInfluencerV2StatusEnum,
  SORT_ORDER,
} from "@entities/brief/lib/constants";
import { useGetBrIefById } from "@entities/brief/lib/hooks/useGetBriefById";
import { useGetBriefInfluencersV2 } from "@entities/brief/lib/hooks/useGetBriefInfluencersV2";
import { useGetBriefsByInfId } from "@entities/brief/lib/hooks/useGetBriefsByInfId";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import useGetPricingPocFacets from "@entities/pricingWorkload/lib/hooks/useGetPricingPocFacets";
import useGetPricingWorkloads from "@entities/pricingWorkload/lib/hooks/useGetPricingWorkloads";
import { ContactFilterValue } from "@features/BriefContactFilter/BriefContactFilterV2";
import { ContactsModalState } from "@features/closureDetail/hooks/useClosureDetailColumns";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import {
  IBriefInfluencerV2,
  WhatsappReachoutStatus,
} from "@shared/lib/types/briefInfluencerV2";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";

export const PRICING_TABS = [
  { value: "All", label: "All", countKey: "all" },
  { value: "Initial Costs", label: "Initial cost", countKey: "initalCost" },
  {
    value: "Negotiations Requested",
    label: "Negotiation requested",
    countKey: "negoRequested",
  },
  {
    value: "Commercials Sent to BM",
    label: "Commercial sent to BM",
    countKey: "sentToBM",
  },
] as const;

export const usePricingProject = () => {
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isLoadingRef = useRef(false);

  const [sortBy, setSortBy] = useState<
    | {
        sortByDynamicFields?: {
          [key: string]: SORT_ORDER;
        };
      }
    | undefined
  >(undefined);

  const [dynamicFields, setDynamicFields] = useState<{
    [key: string]: {
      equalTo?: string[];
      range?: {
        from: number;
        to: number;
      };
    };
  }>({});

  const setDynamicField = (
    field: string,
    value?: string[] | { from: number; to: number }
  ) => {
    if (value) {
      const existingField = dynamicFields[field];
      const isArray = Array.isArray(value);
      const isRange = !isArray && "from" in value && "to" in value;
      setDynamicFields({
        ...dynamicFields,
        [field]: {
          equalTo: isArray ? value : existingField?.equalTo || undefined,
          range: isRange ? value : existingField?.range || undefined,
        },
      });
    } else {
      const newDynamicFields = { ...dynamicFields };
      delete newDynamicFields[field];
      setDynamicFields(newDynamicFields);
    }
  };

  const [segmentedValue, setSegmentedValue] = useState<string | number>("All");
  const [searchParams, setSearchParams] = useSearchParams();

  const { userDetails, isFetching: isFetchingUserDetails } = useGetMe();

  const [selectedInfluencer, setSelectedInfluencer] =
    useState<IBriefInfluencerV2 | null>(null);
  const [socialMediaInfluencer, setSocialMediaInfluencer] =
    useState<IBriefInfluencerV2 | null>(null);

  const [deliverableModalProps, setDeliverableModalProps] = useState<{
    visible: boolean;
    deliverableId?: string;
    briefInfluencerIds: string[];
    type: "view" | "create";
  }>({
    visible: false,
    deliverableId: "",
    briefInfluencerIds: [],
    type: "create",
  });

  const userId = searchParams.get("userId");

  const { pricingWorkloadData, isPricingWorkloadLoading } =
    useGetPricingWorkloads({
      userIds: userId ? [userId] : [],
    });

  const { briefId } = useParams();
  const { setBrief, setBriefIdOnly, setIsLoadingBrief } = useBriefStore();

  const [pageNo, setPageNo] = useState<number>(1);

  const { brief, isFetchingBrief } = useGetBrIefById(briefId || "");

  useEffect(() => {
    if (briefId) {
      setBriefIdOnly(briefId);
    }
  }, [briefId, setBriefIdOnly]);

  useEffect(() => {
    if (briefId && brief) {
      setBrief(brief, briefId);
    }
  }, [brief, briefId, setBrief]);

  useEffect(() => {
    setIsLoadingBrief(Boolean(isFetchingBrief));
  }, [isFetchingBrief, setIsLoadingBrief]);

  useEffect(() => {
    if (briefId && brief) {
      // Update meta description to brief name
      document.title = brief.name;
      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );
      if (metaDescription) {
        metaDescription.setAttribute("content", brief.name);
      } else {
        const newMetaDescription = document.createElement("meta");
        newMetaDescription.name = "description";
        newMetaDescription.content = brief.name;
        document.head.appendChild(newMetaDescription);
      }
    }
    return () => {
      document.title = "Cloutflow";
      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );
      if (metaDescription) {
        metaDescription.setAttribute("content", "Cloutflow");
      } else {
        const newMetaDescription = document.createElement("meta");
        newMetaDescription.name = "description";
        newMetaDescription.content = "Cloutflow";
        document.head.appendChild(newMetaDescription);
      }
    };
  }, [brief, briefId]);

  const { pricingPocFacets } = useGetPricingPocFacets(
    briefId || "",
    userId || ""
  );

  const [searchQuery, setSearchQuery] = useState<string>("");

  const [reachoutStatusFilter, setReachoutStatusFilter] = useState<
    WhatsappReachoutStatus[]
  >([]);

  const [contactFilter, setContactFilter] = useState<ContactFilterValue>({
    primaryContact: { type: [], agencyId: [] },
  });

  const [deliverableFilter, setDeliverableFilter] = useState<string[]>([]);

  const [createdAtFilter, setCreatedAtFilter] = useState<
    { from?: string; to?: string } | undefined
  >(undefined);
  const [updatedAtFilter, setUpdatedAtFilter] = useState<
    { from?: string; to?: string } | undefined
  >(undefined);
  const [sortByCreatedAt, setSortByCreatedAt] = useState<
    SORT_ORDER | undefined
  >(undefined);
  const [sortByUpdatedAt, setSortByUpdatedAt] = useState<
    SORT_ORDER | undefined
  >(undefined);

  const [isShortlistedFilter, setIsShortlistedFilter] = useState<
    boolean | undefined
  >(undefined);

  const isContactFilterActive =
    contactFilter.isCreatorOnboardingPending === true ||
    contactFilter.primaryContact.type.length > 0 ||
    contactFilter.primaryContact.agencyId.length > 0;

  const [debouncedSearchQuery] = useDebounce(searchQuery, 250);

  useEffect(() => {
    setReachoutStatusFilter([]);
    setContactFilter({ primaryContact: { type: [], agencyId: [] } });
    setDeliverableFilter([]);
    setCreatedAtFilter(undefined);
    setUpdatedAtFilter(undefined);
    setSortByCreatedAt(undefined);
    setSortByUpdatedAt(undefined);
    setIsShortlistedFilter(undefined);
  }, [debouncedSearchQuery, segmentedValue]);

  const handleSetSortBy = useCallback(
    (
      nextSortBy:
        | {
            sortByDynamicFields?: {
              [key: string]: SORT_ORDER;
            };
          }
        | undefined
    ) => {
      setSortBy(nextSortBy);
      setSortByCreatedAt(undefined);
      setSortByUpdatedAt(undefined);
    },
    []
  );

  const handleSetDateColumnSort = useCallback(
    (sort: { sortByCreatedAt?: SORT_ORDER; sortByUpdatedAt?: SORT_ORDER }) => {
      setSortByCreatedAt(sort.sortByCreatedAt);
      setSortByUpdatedAt(sort.sortByUpdatedAt);
      setSortBy(undefined);
    },
    []
  );

  const [contactsModalProps, setContactsModalProps] =
    useState<ContactsModalState>({
      visible: false,
      briefInfluencerId: "",
      creatorInfo: undefined,
      conatcts: [],
      addContactOnly: false,
      initialContact: null,
    });

  const [openPocForm, setOpenPocForm] = useState<{
    notes: string;
    id: string;
  } | null>(null);

  const filters = {
    briefId: briefId || "",
    statuses: [
      BreifInfluencerV2StatusEnum.Shortlisted,
      BreifInfluencerV2StatusEnum.Pricing,
    ],
    assignedPricingPocV2: userId ? [userId] : undefined,
    isShortlisted: isShortlistedFilter,
    isCreatorOnboardPending: contactFilter.isCreatorOnboardingPending
      ? true
      : segmentedValue === "All"
        ? false
        : undefined,
    primaryContact:
      contactFilter.primaryContact.type.length > 0 ||
      contactFilter.primaryContact.agencyId.length > 0
        ? {
            type: contactFilter.primaryContact.type,
            agencyId: contactFilter.primaryContact.agencyId,
          }
        : undefined,
    searchQuery: debouncedSearchQuery,
    influencerCost: {
      offerStatus:
        segmentedValue === "Initial Costs"
          ? ["offer-yet-to-be-sent", "offer-sent"]
          : segmentedValue === "Commercials Sent to BM"
            ? ["offer-accepted", "negotiations-accepted"]
            : segmentedValue === "Negotiations Requested"
              ? ["negotiations-requested", "negotiations-sent"]
              : undefined,
    },
    limit: 10,
    page: pageNo,
    isBrandDeclined: segmentedValue === "All" ? undefined : false,
    isCreatorDeclined: segmentedValue === "All" ? undefined : false,
    pricingWhatsappReachout: { statuses: reachoutStatusFilter },
    isDeliverablesPending: deliverableFilter.includes("") ? true : undefined,
    deliverableV2Ids:
      deliverableFilter.filter((id) => id !== "").length > 0
        ? deliverableFilter.filter((id) => id !== "")
        : undefined,
    dynamicFields,
    sortByDynamicFields: sortBy?.sortByDynamicFields,
    sortByCreatedAt,
    sortByUpdatedAt,
    createdAt:
      createdAtFilter?.from || createdAtFilter?.to
        ? createdAtFilter
        : undefined,
    updatedAt:
      updatedAtFilter?.from || updatedAtFilter?.to
        ? updatedAtFilter
        : undefined,
  };

  const {
    briefInfluencers,
    pagination,
    isFetching: isFetchingBriefInfluencers,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isLoading,
  } = useGetBriefInfluencersV2(filters, !!userId && !!pricingWorkloadData);

  const {
    briefInfluencers: influencerCampaignHistory,
    isFetching: isFetchingInfluencerCampaignHistory,
  } = useGetBriefsByInfId({
    socialMediaObjectIds: selectedInfluencer?.socialMediaObjectId
      ? [selectedInfluencer?.socialMediaObjectId]
      : undefined,
  });

  const onLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const handleScroll = useCallback(() => {
    const bodyEl =
      scrollRef.current?.querySelector<HTMLDivElement>(
        ".ant-table-tbody-virtual-holder"
      ) ?? scrollRef.current?.querySelector<HTMLDivElement>(".ant-table-body");
    if (!bodyEl || isFetchingNextPage || isLoadingRef.current) return;

    const nearBottom =
      bodyEl.scrollTop + bodyEl.clientHeight >= bodyEl.scrollHeight - 120;
    if (nearBottom) {
      isLoadingRef.current = true;
      onLoadMore();
    }
  }, [isFetchingNextPage, onLoadMore]);

  useEffect(() => {
    if (!isFetchingNextPage) {
      isLoadingRef.current = false;
    }
  }, [isFetchingNextPage]);

  useEffect(() => {
    const body =
      scrollRef.current?.querySelector<HTMLDivElement>(
        ".ant-table-tbody-virtual-holder"
      ) ?? scrollRef.current?.querySelector<HTMLDivElement>(".ant-table-body");
    if (!body) return;
    body.addEventListener("scroll", handleScroll);
    return () => body.removeEventListener("scroll", handleScroll);
  }, [handleScroll, isFetchingBriefInfluencers, briefInfluencers]);

  useEffect(() => {
    setPageNo(1);
    setOpenPocForm(null);
  }, [
    segmentedValue,
    debouncedSearchQuery,
    contactFilter,
    deliverableFilter,
    createdAtFilter,
    updatedAtFilter,
    sortByCreatedAt,
    sortByUpdatedAt,
    isShortlistedFilter,
  ]);

  useEffect(() => {
    if (!userId) setSearchParams({ userId: userDetails?.userEntity?.id || "" });
  }, [userDetails, userId, setSearchParams]);

  return {
    queryClient,
    scrollRef,
    segmentedValue,
    setSegmentedValue,
    searchParams,
    setSearchParams,
    userDetails,
    isFetchingUserDetails,
    selectedInfluencer,
    setSelectedInfluencer,
    socialMediaInfluencer,
    setSocialMediaInfluencer,
    deliverableModalProps,
    setDeliverableModalProps,
    userId,
    pricingWorkloadData,
    isPricingWorkloadLoading,
    briefId,
    pageNo,
    setPageNo,
    brief,
    pricingPocFacets,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    contactsModalProps,
    setContactsModalProps,
    openPocForm,
    setOpenPocForm,
    briefInfluencers,
    pagination,
    isFetchingBriefInfluencers,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isLoading,
    influencerCampaignHistory,
    isFetchingInfluencerCampaignHistory,
    filters,
    reachoutStatusFilter,
    setReachoutStatusFilter,
    contactFilter,
    setContactFilter,
    isContactFilterActive,
    deliverableFilter,
    setDeliverableFilter,
    isShortlistedFilter,
    setIsShortlistedFilter,
    sortBy,
    setSortBy: handleSetSortBy,
    createdAtFilter,
    setCreatedAtFilter,
    updatedAtFilter,
    setUpdatedAtFilter,
    sortByCreatedAt,
    sortByUpdatedAt,
    setDateColumnSort: handleSetDateColumnSort,
    dynamicFields,
    setDynamicFields,
    setDynamicField,
  };
};
